import React, { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, addHours } from "date-fns";
import { makeStyles } from 'tss-react/mui';
import { alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import { Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { Archive, Unarchive, PushPin, PushPinOutlined, ImportExport, Event, Campaign } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AndroidIcon from "@mui/icons-material/Android";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TicketMessagesDialog from "../TicketMessagesDialog";
import DoneIcon from '@mui/icons-material/Done';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { Facebook, Instagram, Language, Science } from "@mui/icons-material";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ScheduleModal from "../ScheduleModal";

const useStyles = makeStyles()((theme) => ({
  ticket: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px 8px 16px",
    minHeight: 68,
    animation: "ticketFadeIn 0.25s ease",
    "&.Mui-selected": {
      backgroundColor: "rgba(161, 58, 58, 0.06)",
      "&:hover": {
        backgroundColor: "rgba(161, 58, 58, 0.09)",
      },
    },
  },

  "@keyframes ticketFadeIn": {
    from: { opacity: 0, transform: "translateY(-4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },

  pendingTicket: {
    cursor: "unset",
  },

  ticketQueueColor: {
    flex: "none",
    width: 4,
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },

  avatar: {
    width: 46,
    height: 46,
  },

  body: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  topRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  name: {
    flex: 1,
    minWidth: 0,
    fontSize: 14.5,
    fontWeight: 500,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  time: {
    flexShrink: 0,
    fontSize: 11.5,
    color: theme.palette.text.secondary,
  },

  midRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  midRowPending: {
    paddingRight: 138,
  },

  preview: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "& p": {
      margin: 0,
      display: "inline",
    },
  },

  previewUnread: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },

  unread: {
    flexShrink: 0,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#25d366",
    color: "#fff",
    fontSize: 11.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
  },

  chip: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "16px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === "light" ? "#eef1f4" : "rgba(255,255,255,0.08)",
    padding: "0px 7px",
    borderRadius: 8,
    whiteSpace: "nowrap",
    maxWidth: 90,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  actions: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },

  pendingActions: {
    position: "absolute",
    right: 12,
    bottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  acceptButton: {
    height: 26,
    padding: "0 14px",
    borderRadius: 14,
    textTransform: "none",
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: "nowrap",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.45)}`,
    transition: "all 0.15s ease",
    "& .MuiButton-startIcon": {
      marginRight: 4,
      marginLeft: 0,
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.55)}`,
    },
    "&.Mui-disabled": {
      opacity: 0.6,
      color: theme.palette.primary.contrastText,
    },
  },

  eyeButton: {
    padding: 3,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

const channelIcon = (channel, size = 15) => {
  const style = { fontSize: size, flexShrink: 0 };
  switch (channel) {
    case "facebook":
      return <Facebook style={{ ...style, color: "#3b5998" }} />;
    case "instagram":
      return <Instagram style={{ ...style, color: "#e1306c" }} />;
    case "webchat":
      return <Language style={{ ...style, fontSize: size * 0.82, color: "#3956ff" }} />;
    case "sandbox":
      return <Science style={{ ...style, fontSize: size * 0.82, color: "#8b8f9a" }} />;
    case "whatsapp_cloud":
      return <WhatsAppIcon style={{ ...style, color: "#25d3c1" }} />;

    case "whatsapp":
    default:
      return <WhatsAppIcon style={{ ...style, color: "#25d366" }} />;
  }
};

const ChannelBadge = ({ channel }) => (
  <span
    style={{
      width: 18,
      height: 18,
      borderRadius: "50%",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 0 3px rgba(0,0,0,0.35)",
    }}
  >
    {channelIcon(channel, 13)}
  </span>
);

const profileBadge = (type) => {
  const style = {
    fontSize: 15,
    color: "#fff",
    backgroundColor: "#3498db",
    borderRadius: "50%",
    padding: 2,
  };
  switch (type) {
    case "PROSPECTO":
      return (
        <Tooltip title="Prospecto">
          <EmojiPeopleIcon style={style} />
        </Tooltip>
      );
    case "CLIENTE":
      return (
        <Tooltip title="Cliente">
          <HowToRegIcon style={{ ...style, backgroundColor: "#27ae60" }} />
        </Tooltip>
      );
    default:
      return null;
  }
};

const TicketListItemCustom = ({ ticket }) => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const { profile } = user;

  const ticketUser = ticket.userId && ticket.user ? ticket.user.name : null;
  const whatsAppName = ticket.whatsappId && ticket.whatsapp ? ticket.whatsapp.name : null;
  const conectionName = ticket.whatsappId && ticket.whatsapp ? ticket.channel : null;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    navigate(`/tickets/${ticket.uuid}`);
    setLoading(false);
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleOpenContextMenu = (e) => {
    e.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: e.clientX - 2, mouseY: e.clientY - 4 }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleToggleArchive = async () => {
    handleCloseContextMenu();
    try {
      await api.put(`/tickets/${ticket.id}/archive`, { archived: !ticket.archived });
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleFavorite = async () => {
    handleCloseContextMenu();
    try {
      if (ticket.status === "favorite") {
        await api.put(`/tickets/${ticket.id}`, { status: "pending", userId: null });
      } else {
        await api.put(`/tickets/${ticket.id}`, { status: "favorite", userId: user?.id });
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTransferModal = () => {
    handleCloseContextMenu();
    setTransferModalOpen(true);
  };

  const handleOpenScheduleModal = () => {
    handleCloseContextMenu();
    setScheduleModalOpen(true);
  };

  const contactBadge = profileBadge(ticket?.contact?.category?.name ?? '');
  const isPending = ticket.status === "pending" || ticket.status === "bot";

  const isMetaChannelWithoutTemplates =
    ticket.channel === "facebook" || ticket.channel === "instagram";
  const replyWindowExpired =
    isMetaChannelWithoutTemplates && ticket.updatedAt
      ? new Date(addHours(parseISO(ticket.updatedAt), 24).getTime() - 10 * 60 * 1000) <
        new Date()
      : false;

  return (
    <React.Fragment key={ticket.id}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      ></TicketMessagesDialog>
      <ListItemButton
        dense
        onClick={(e) => {
          if (ticket.status === "pending" || ticket.status === 'bot') return;
          handleSelectTicket(ticket);
        }}
        onContextMenu={handleOpenContextMenu}
        selected={ticketId && +ticketId === ticket.id}
        className={cx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending" || ticket.status === 'bot' || ticket.chatbot,
        })}>

        <Tooltip
          arrow
          placement="right"
          title={ticket.queue?.name || "Sin departamento"}
        >
          <span
            style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
            className={classes.ticketQueueColor}
          ></span>
        </Tooltip>

        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={<ChannelBadge channel={conectionName} />}
        >
          {contactBadge ? (
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              badgeContent={contactBadge}
            >
              <Avatar className={classes.avatar} src={ticket?.contact?.profilePicUrl} />
            </Badge>
          ) : (
            <Avatar className={classes.avatar} src={ticket?.contact?.profilePicUrl} />
          )}
        </Badge>

        <div className={classes.body}>
          {}
          <div className={classes.topRow}>
            <span className={classes.name}>{ticket?.contact?.name}</span>
            {ticket.lastMessage && (
              <span className={classes.time}>
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
              </span>
            )}
          </div>

          {}
          <div className={cx(classes.midRow, { [classes.midRowPending]: isPending })}>
            <span
              className={cx(
                classes.preview,
                ticket.unreadMessages > 0 && classes.previewUnread
              )}
            >
              {ticket?.lastMessage?.includes('data:image') ? (
                <MarkdownWrapper>Localización</MarkdownWrapper>
              ) : (
                <MarkdownWrapper>{ticket.lastMessage || ""}</MarkdownWrapper>
              )}
            </span>

            <div className={classes.actions}>
              {ticketUser && !isPending && (
                <span className={classes.chip}>{ticketUser}</span>
              )}
              {ticket.whatsappId && whatsAppName && !isPending && (
                <span className={classes.chip}>{whatsAppName}</span>
              )}
              {ticket.adReferralSourceId && (
                <Tooltip title={`Anuncio: ${ticket.adReferralCampaign || ticket.adReferralTitle || "Meta"}${ticket.adReferralSource ? ` (${ticket.adReferralSource})` : ""}`}>
                  <Campaign style={{ fontSize: 16, color: "#00a884" }} />
                </Tooltip>
              )}
              {ticket.chatbot && !isPending && (
                <Tooltip title="Chatbot">
                  <AndroidIcon style={{ fontSize: 15, color: grey[600] }} />
                </Tooltip>
              )}
              {ticket.status === "closed" && (
                <span
                  className={classes.chip}
                  style={{ color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.08)" }}
                >
                  Cerrado
                </span>
              )}
              {}
              {profile === "admin" && !isPending && (
                <Tooltip title="Ver conversación">
                  <IconButton
                    size="small"
                    className={classes.eyeButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenTicketMessageDialog(true);
                    }}
                  >
                    <VisibilityIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {ticket.unreadMessages > 0 && !isPending && (
                <span className={classes.unread}>{ticket.unreadMessages}</span>
              )}
            </div>
          </div>
        </div>

        {}
        {isPending && (
          <div className={classes.pendingActions}>
            {profile === "admin" && (
              <Tooltip title="Ver conversación">
                <IconButton
                  size="small"
                  className={classes.eyeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTicketMessageDialog(true);
                  }}
                >
                  <VisibilityIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                replyWindowExpired
                  ? "Pasó el tiempo para responder por chat: puedes abrir y cerrar este chat para gestionarlo, pero no escribirle hasta que el contacto vuelva a escribir (o le envíes una plantilla)."
                  : ""
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disableRipple
                  disabled={loading}
                  className={classes.acceptButton}
                  startIcon={<DoneIcon style={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcepptTicket(ticket.id);
                  }}
                >
                  Aceptar
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </ListItemButton>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleToggleArchive}>
          <ListItemIcon>
            {ticket.archived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{ticket.archived ? "Desarchivar" : "Archivar"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleFavorite}>
          <ListItemIcon>
            {ticket.status === "favorite" ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{ticket.status === "favorite" ? "Quitar de Favoritos" : "Agregar a Favoritos"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenTransferModal}>
          <ListItemIcon>
            <ImportExport fontSize="small" />
          </ListItemIcon>
          <ListItemText>Transferir</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenScheduleModal}>
          <ListItemIcon>
            <Event fontSize="small" />
          </ListItemIcon>
          <ListItemText>Agendar</ListItemText>
        </MenuItem>
      </Menu>
      <TransferTicketModalCustom
        modalOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        ticketid={ticket.id}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        aria-labelledby="form-dialog-title"
        contactId={ticket?.contact?.id}
      />
      <Divider component="li" style={{ marginLeft: 74 }} />
    </React.Fragment>
  );
};

export default TicketListItemCustom;
